/**
 * Enterprise-grade fetch wrapper with dual-token refresh logic,
 * timeout handling, and automatic AbortController management.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://octoink-backend.onrender.com';

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token) => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
};

const onTokenRefreshFailed = () => {
    refreshSubscribers.forEach((cb) => cb(null));
    refreshSubscribers = [];
};

export const fetchWithRetry = async (url, options = {}, retries = 3, delay = 2000, timeout = 15000) => {
    const controller = options.signal ? null : new AbortController();
    const signal = options.signal || controller.signal;
    
    let lastError;
    
    for (let i = 0; i < retries; i++) {
        let timeoutId;
        if (controller) timeoutId = setTimeout(() => controller.abort(), timeout);
        
        try {
            const token = localStorage.getItem('token');
            // Create a local copy of headers to avoid mutating the original options object
            const headers = { ...options.headers };
            if (token && !headers.Authorization) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(url, { ...options, headers, signal });
            if (timeoutId) clearTimeout(timeoutId);
            
            if (response.status === 401 && !url.includes('/api/auth/refresh')) {
                const refreshToken = localStorage.getItem('refreshToken');
                
                if (!refreshToken) {
                    throw new Error('AUTH_ERROR: No refresh token');
                }

                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ refreshToken })
                        });
                        
                        if (refreshRes.ok) {
                            const data = await refreshRes.json();
                            localStorage.setItem('token', data.token);
                            localStorage.setItem('refreshToken', data.refreshToken);
                            isRefreshing = false;
                            onTokenRefreshed(data.token);
                        } else {
                            throw new Error('REFRESH_FAILED');
                        }
                    } catch (e) {
                        isRefreshing = false;
                        onTokenRefreshFailed(); // Notify others that we failed
                        localStorage.removeItem('token');
                        localStorage.removeItem('refreshToken');
                        throw new Error('AUTH_ERROR: Refresh failed');
                    }
                }

                const newToken = await new Promise((resolve, reject) => {
                    subscribeTokenRefresh((token) => {
                        if (token) resolve(token);
                        else reject(new Error('AUTH_ERROR: Refresh failed'));
                    });
                });

                return fetchWithRetry(url, {
                    ...options,
                    headers: { ...options.headers, 'Authorization': `Bearer ${newToken}` }
                }, 1);
            }
            // ───────────────────────────────────────────────

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }
            
            return await response.json();
        } catch (err) {
            if (timeoutId) clearTimeout(timeoutId);
            if (err.name === 'AbortError') throw new Error('Request timeout');
            if (err.message.includes('AUTH_ERROR')) throw err;
            
            lastError = err;
            if (i < retries - 1) await new Promise(r => setTimeout(r, delay));
        }
    }
    
    throw lastError || new Error('Request failed');
};

/**
 * Helper to normalize image URLs (handles Cloudinary, local uploads, and external links)
 */
// Default placeholder SVG for missing product images
const PLACEHOLDER_IMAGE = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect fill="#0d0d18" width="400" height="400"/><rect fill="#14142a" x="100" y="100" width="200" height="200" rx="24"/><path d="M175 220l25-40 25 40z M230 220l20-25 20 25z" fill="#2a2a4a"/><circle cx="240" cy="160" r="15" fill="#2a2a4a"/><text x="200" y="340" text-anchor="middle" fill="#3a3a5a" font-family="sans-serif" font-size="14" font-weight="600">No Image Available</text></svg>`)}`;

export const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (typeof url !== 'string') return PLACEHOLDER_IMAGE;
    
    if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
        return url;
    }
    
    // Normalize path
    let cleanPath = url.replace(/\\/g, '/');
    
    // Auto-upgrade local static images to webp since we converted them
    const localStaticPrefixes = ['/portfolio/', 'portfolio/', '/client', 'client/', '/assets/', 'assets/'];
    if (localStaticPrefixes.some(p => cleanPath.startsWith(p))) {
        cleanPath = cleanPath.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    }
    
    // If it's a known local asset prefix, return as is (relative to current host root)
    const localPrefixes = ['/portfolio', 'portfolio/', '/client', 'client/', '/assets', 'assets/', '/logo.svg'];
    if (localPrefixes.some(p => cleanPath.startsWith(p))) {
        return cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath;
    }

    // Otherwise assume it's a backend asset
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    const path = cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath;
    
    return `${base}${path}`;
};
